import React, { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import MobileNavBar from '../common/MobileNavBar';
import MobileFooter from '../MobileFooter';
import MobileBottomNav from '../common/MobileBottomNav';
import { convertToVwMob } from '../../Utils/responsive';
import { useAppSelector } from '../../store/hooks';
import { selectUserProfile } from '../../Module/User/Presentation/userSelectors';
import KakaoChannelModal from '../common/KakaoChannelModal';
import ProfileIncompleteModal from '../common/ProfileIncompleteModal';
import { useLocation } from 'react-router-dom';

const KAKAO_MODAL_SHOWN_STORAGE_KEY = 'missionfund.kakaoModalSeenAfterLogin';
const SUPPRESS_POST_SIGNUP_MODALS_KEY = 'missionfund.suppressPostSignupModals';

type Props = {
  title?: string;
  children: ReactNode;
  footerRef?: React.Ref<HTMLElement>;
  hideHeader?: boolean;
  hideFooter?: boolean;
  showBottomNav?: boolean;
};

const MobilePageLayout: React.FC<Props> = ({
  title,
  children,
  footerRef,
  hideHeader,
  hideFooter,
  showBottomNav = true,
}) => {
  const location = useLocation();
  const userProfile = useAppSelector(selectUserProfile);
  const [isKakaoModalOpen, setIsKakaoModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const suppressPostSignupModals = window.localStorage.getItem(SUPPRESS_POST_SIGNUP_MODALS_KEY) === '1';
      if (suppressPostSignupModals) {
        setIsKakaoModalOpen(false);
        setIsProfileModalOpen(false);
        return;
      }
    }

    // 로그인, 회원가입 페이지에서는 카카오/프로필 모달 띄우지 않음
    if (location.pathname === '/login' || location.pathname === '/signup') {
      setIsKakaoModalOpen(false);
      setIsProfileModalOpen(false);
      return;
    }

    // sns_id가 없으면 항상 모달 표시 (모바일)
    if (userProfile) {
      if (!userProfile.sns_id) {
        let shouldOpen = true;
        if (typeof window !== 'undefined') {
          const hasShown = localStorage.getItem(KAKAO_MODAL_SHOWN_STORAGE_KEY) === '1';
          shouldOpen = !hasShown;
        }
        if (shouldOpen) {
          if (typeof window !== 'undefined') {
            localStorage.setItem(KAKAO_MODAL_SHOWN_STORAGE_KEY, '1');
          }
          setIsKakaoModalOpen(true);
        } else {
          setIsKakaoModalOpen(false);
          checkProfileCompleteness();
        }
      } else {
        setIsKakaoModalOpen(false);
        checkProfileCompleteness();
      }
    } else {
      // 로그아웃 상태 등 userProfile이 없으면 모달 닫기
      setIsKakaoModalOpen(false);
      setIsProfileModalOpen(false);
    }
  }, [userProfile, location.pathname]);

  const checkProfileCompleteness = () => {
    if (userProfile) {
       const isComplete = !!userProfile.authentication_by;
       const isInProgress = !!(userProfile.authentication_image_url || userProfile.registration_card_url);
       
       if (!isComplete && !isInProgress) {
         if (location.pathname !== '/account') {
            setIsProfileModalOpen(true);
         }
       } else {
         setIsProfileModalOpen(false);
       }
    }
  };

  const handleCloseKakaoModal = () => {
    setIsKakaoModalOpen(false);
    checkProfileCompleteness();
  };

  const handleCloseProfileModal = () => {
    setIsProfileModalOpen(false);
  };

  return (
    <PageBackground>
      <Shell>
        {!hideHeader && <MobileNavBar title={title} />}
        <Main showBottomNav={showBottomNav}>
          <Content>{children}</Content>
          {!hideFooter && <MobileFooter ref={footerRef} showBottomNav={showBottomNav} />}
        </Main>
        {showBottomNav && <MobileBottomNav />}
      </Shell>
      <KakaoChannelModal isOpen={isKakaoModalOpen} onClose={handleCloseKakaoModal} />
      <ProfileIncompleteModal isOpen={isProfileModalOpen} onClose={handleCloseProfileModal} />
    </PageBackground>
  );
};

const PageBackground = styled.div`
  height: 100dvh;
  background-color: #F9FAFB;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Shell = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Main = styled.main<{ showBottomNav: boolean }>`
  flex: 1;
  padding: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const Content = styled.div`
  width: 100%;
  min-height: 40vh;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(12)};
  color: ${({ theme }) => theme.colors.grayScale[80]};
  padding: ${convertToVwMob(16)} ${convertToVwMob(16)} ${convertToVwMob(300)};
`;

export default MobilePageLayout;


